import React, { useEffect, useRef, useState } from 'react';
import '../styles/Pages/About.css';

/* ══════════════════════════════════════
   INTERSECTION OBSERVER HOOK
══════════════════════════════════════ */
function useInView(options = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); io.disconnect(); }
    }, { threshold: 0.15, ...options });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return [ref, inView];
}

/* ══════════════════════════════════════
   ANIMATED COUNTER
══════════════════════════════════════ */
function Counter({ to, suffix = '' }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const fired = useRef(false);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !fired.current) {
        fired.current = true;
        const t0 = performance.now(), dur = 1800;
        const run = (now) => {
          const p = Math.min((now - t0) / dur, 1);
          const ease = 1 - (1 - p) ** 3;
          setVal(Math.round(ease * to));
          if (p < 1) requestAnimationFrame(run);
        };
        requestAnimationFrame(run);
      }
    }, { threshold: 0.5 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [to]);
  return <span ref={ref}>{val}{suffix}</span>;
}

/* ══════════════════════════════════════
   PARTICLE CANVAS (reused pattern)
══════════════════════════════════════ */
function ParticleField({ density = 50 }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W, H, pts, raf;
    const resize = () => { W = canvas.width = canvas.offsetWidth; H = canvas.height = canvas.offsetHeight; };
    const make = () => ({ x: Math.random()*W, y: Math.random()*H, vx:(Math.random()-.5)*.25, vy:(Math.random()-.5)*.25, r:Math.random()*1.3+.3, a:Math.random()*.4+.07 });
    const init = () => { resize(); pts = Array.from({length:density},make); };
    const draw = () => {
      ctx.clearRect(0,0,W,H);
      pts.forEach(p => {
        p.x+=p.vx; p.y+=p.vy;
        if(p.x<0)p.x=W; if(p.x>W)p.x=0; if(p.y<0)p.y=H; if(p.y>H)p.y=0;
        ctx.beginPath(); ctx.arc(p.x,p.y,p.r,0,Math.PI*2);
        ctx.fillStyle=`rgba(47,128,237,${p.a})`; ctx.fill();
      });
      for(let i=0;i<pts.length;i++) for(let j=i+1;j<pts.length;j++) {
        const dx=pts[i].x-pts[j].x, dy=pts[i].y-pts[j].y, d=Math.sqrt(dx*dx+dy*dy);
        if(d<90){ctx.beginPath();ctx.moveTo(pts[i].x,pts[i].y);ctx.lineTo(pts[j].x,pts[j].y);ctx.strokeStyle=`rgba(47,128,237,${.1*(1-d/90)})`;ctx.lineWidth=.5;ctx.stroke();}
      }
      raf=requestAnimationFrame(draw);
    };
    init(); draw();
    window.addEventListener('resize',init);
    return ()=>{cancelAnimationFrame(raf);window.removeEventListener('resize',init);};
  },[density]);
  return <canvas ref={canvasRef} className="about-canvas" aria-hidden />;
}

/* ══════════════════════════════════════
   CUSTOM ICONS
══════════════════════════════════════ */
const IconInnovation = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
);
const IconSecurity = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);
const IconPartnership = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const IconExcellence = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);
const IconEngineering = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="1"/>
    <path d="M12 8v8M8 12h8"/>
    <circle cx="12" cy="12" r="9"/>
    <path d="M5.64 5.64a8 8 0 0 0 0 11.31M18.36 5.64a8 8 0 0 1 0 11.31"/>
  </svg>
);
const IconDataScience = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
    <circle cx="8" cy="18" r="1"/>
    <circle cx="16" cy="18" r="1"/>
    <path d="M12 22v-4"/>
  </svg>
);
const IconStrategy = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M3 12h18M3 18h18"/>
    <circle cx="7" cy="6" r="2"/>
    <circle cx="17" cy="12" r="2"/>
    <circle cx="6" cy="18" r="2"/>
  </svg>
);
const IconTrust = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    <path d="M10 12l2 2 4-4"/>
  </svg>
);

const valueIcons = [<IconInnovation/>, <IconSecurity/>, <IconPartnership/>, <IconExcellence/>];
const expertiseIcons = [<IconEngineering/>, <IconDataScience/>, <IconStrategy/>, <IconTrust/>];

const whyIcons = ['🤖','🏢','🔄','⏰','🔒','🧪'];

/* avatar initials colors */
const avatarColors = [
  'linear-gradient(135deg,#2f80ed,#56ccf2)',
  'linear-gradient(135deg,#7c6fe0,#a78bfa)',
  'linear-gradient(135deg,#10b981,#34d399)',
  'linear-gradient(135deg,#f59e0b,#fbbf24)',
];

/* ══════════════════════════════════════
   SECTION HEADER COMPONENT
══════════════════════════════════════ */
function SectionHeader({ eyebrow, title, accent, subtitle }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} className={`section-header ${inView ? 'in-view' : ''}`}>
      {eyebrow && <span className="section-eyebrow">{eyebrow}</span>}
      <h2 className="section-title">
        {title} {accent && <span className="section-accent">{accent}</span>}
      </h2>
      {subtitle && <p className="section-subtitle">{subtitle}</p>}
    </div>
  );
}

/* ══════════════════════════════════════
   ABOUT PAGE
══════════════════════════════════════ */
const About = () => {
  const coreExpertise = [
    { title: 'Engineering Excellence', description: 'Specialized in scaling distributed systems and cloud-native AI architectures.' },
    { title: 'Data Science & Research', description: 'Experts in deep learning, NLP, and custom LLM fine-tuning.' },
    { title: 'Product Strategy', description: 'Bridging the gap between complex AI capabilities and measurable business outcomes.' },
    { title: 'Ethical AI Governance', description: 'Committed to building transparent, secure, and unbiased intelligence systems.' },
  ];

  const timeline = [
    { year: '2011', event: 'Founded with a vision to merge AI and human expertise', icon: '🚀' },
    { year: '2014', event: 'Launched first AI-powered SaaS platform to market',      icon: '⚡' },
    { year: '2017', event: 'Expanded to cloud engineering and infrastructure',        icon: '☁️' },
    { year: '2019', event: 'Reached 50+ enterprise clients globally',                 icon: '🌍' },
    { year: '2021', event: 'Introduced advanced AI agent orchestration systems',      icon: '🤖' },
    { year: '2024', event: 'Industry leader with 500+ successful projects delivered', icon: '🏆' },
  ];

  const values = [
    { title: 'Innovation',   description: "We push the boundaries of what's possible with AI — constantly exploring frontier research to give clients a competitive edge." },
    { title: 'Security',     description: 'Enterprise-grade security is architected into every layer we build, not bolted on as an afterthought.' },
    { title: 'Partnership',  description: "Your success is our success. We're committed to long-term relationships built on trust and measurable results." },
    { title: 'Excellence',   description: 'We hold ourselves to the highest standards in code quality, system reliability, and service delivery.' },
  ];

  const stats = [
    { to: 13,  suffix: '+', label: 'Years of Excellence' },
    { to: 500, suffix: '+', label: 'Projects Delivered' },
    { to: 50,  suffix: '+', label: 'Enterprise Clients' },
    { to: 150, suffix: '+', label: 'Team Members' },
    { to: 6,   suffix: '',  label: 'Global Offices' },
    { to: 98,  suffix: '%', label: 'Client Satisfaction' },
  ];

  const whyCards = [
    { title: 'Deep AI Expertise',      desc: "We don't just use AI — we advance its frontiers through active research." },
    { title: 'Enterprise Experience',  desc: 'Proven track record delivering for Fortune 500 companies worldwide.' },
    { title: 'End-to-End Solutions',   desc: 'From strategy through implementation, deployment, and ongoing support.' },
    { title: '24/7 Support',           desc: 'Always here when you need us — any time zone, any day of the year.' },
    { title: 'Security First',         desc: 'Enterprise-grade security woven into every solution we architect.' },
    { title: 'Innovation Labs',        desc: 'Continuous R&D partnerships keeping you ahead of the technology curve.' },
  ];

  return (
    <div className="about-page">

      {/* ════════════════════ HERO ════════════════════ */}
      <section className="about-hero">
        <ParticleField density={60} />
        <div className="about-grid-bg" aria-hidden />
        <div className="about-glow about-glow-l" aria-hidden />
        <div className="about-glow about-glow-r" aria-hidden />

        <div className="about-hero-inner">
          <div className="about-hero-content">
            <span className="about-eyebrow">
              <span className="eyebrow-dot" />
              Our Story · Est. 2011
            </span>
            <h1 className="about-hero-title">
              About <span className="hero-accent">incogniflex</span>
            </h1>
            <p className="about-hero-sub">
              Leading the convergence of artificial and human intelligence —
              building the infrastructure that powers tomorrow's enterprises.
            </p>
            {/* quick stat pills */}
            <div className="hero-stat-pills">
              {[['13+','Years'],['500+','Projects'],['50+','Clients'],['98%','Satisfaction']].map(([v,l]) => (
                <div key={l} className="hero-pill">
                  <span className="pill-val">{v}</span>
                  <span className="pill-lbl">{l}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* diagonal cut */}
        <div className="hero-cut" aria-hidden />
      </section>

      {/* ════════════════════ MISSION & VISION ════════════════════ */}
      <section className="mission-section">
        <div className="about-container">
          <SectionHeader
            eyebrow="✦ Purpose & Direction"
            title="What Drives"
            accent="Everything We Build"
            subtitle="Two pillars that guide every decision, every line of code, every client engagement."
          />
          <div className="mission-grid">
            {[
              {
                label: 'Mission',
                icon: '🎯',
                color: '--blue',
                text: 'To empower enterprises by seamlessly blending artificial intelligence with human expertise — creating solutions that are smarter, faster, and more resilient than either alone.',
              },
              {
                label: 'Vision',
                icon: '🔭',
                color: '--cyan',
                text: 'A future where AI and humans collaborate as equals, solving the world\'s most complex challenges and creating unprecedented value for businesses and society at large.',
              },
            ].map((m, i) => (
              <MissionCard key={i} {...m} delay={i * 120} />
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ CORE VALUES ════════════════════ */}
      <section className="values-section">
        <div className="about-container">
          <SectionHeader
            eyebrow="◈ Core Principles"
            title="Values That"
            accent="Define Us"
            subtitle="The principles we hold non-negotiable across every project, partnership, and product we ship."
          />
          <div className="values-grid">
            {values.map((v, i) => (
              <ValueCard key={i} value={v} icon={valueIcons[i]} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ TIMELINE ════════════════════ */}
      <section className="timeline-section">
        <div className="about-container">
          <SectionHeader
            eyebrow="📅 Our Journey"
            title="A Decade of"
            accent="Breakthroughs"
            subtitle="13 years of relentless building, learning, and delivering at the frontier of AI."
          />
          <div className="timeline-wrap">
            {/* center spine */}
            <div className="timeline-spine" aria-hidden />
            {timeline.map((item, i) => (
              <TimelineItem key={i} item={item} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ CORE EXPERTISE ════════════════════ */}
      <section className="leadership-section">
        <div className="about-container">
          <SectionHeader
            eyebrow="◈ Our Strengths"
            title="Our Core"
            accent="Expertise"
            subtitle="A multidisciplinary team of engineers, data scientists, and strategists dedicated to enterprise transformation."
          />
          <div className="team-grid">
            {coreExpertise.map((expertise, i) => (
              <ExpertiseCard key={i} expertise={expertise} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ STATS ════════════════════ */}
      <section className="stats-section">
        <div className="about-container">
          <SectionHeader
            eyebrow="📊 By The Numbers"
            title="Impact That"
            accent="Speaks"
          />
          <div className="stats-grid">
            {stats.map((s, i) => (
              <StatCard key={i} stat={s} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ WHY CHOOSE ════════════════════ */}
      <section className="why-section">
        <div className="about-container">
          <SectionHeader
            eyebrow="⚡ Why Us"
            title="Why Choose"
            accent="incogniflex?"
            subtitle="Six reasons our clients stay, scale, and bring their peers."
          />
          <div className="why-grid">
            {whyCards.map((w, i) => (
              <WhyCard key={i} card={w} icon={whyIcons[i]} index={i} />
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

/* ── Sub-components ── */

function MissionCard({ label, icon, text, delay }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`mission-card ${inView ? 'in-view' : ''}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="mission-card-top">
        <span className="mission-icon">{icon}</span>
        <span className="mission-label">{label}</span>
      </div>
      <p className="mission-text">{text}</p>
      <div className="mission-card-line" />
    </div>
  );
}

function ValueCard({ value, icon, index }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`value-card ${inView ? 'in-view' : ''}`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="value-icon-wrap">{icon}</div>
      <h3 className="value-title">{value.title}</h3>
      <p className="value-desc">{value.description}</p>
    </div>
  );
}

function TimelineItem({ item, index }) {
  const [ref, inView] = useInView();
  const isLeft = index % 2 === 0;
  return (
    <div
      ref={ref}
      className={`tl-item ${isLeft ? 'tl-left' : 'tl-right'} ${inView ? 'in-view' : ''}`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="tl-content">
        <div className="tl-year-row">
          <span className="tl-icon">{item.icon}</span>
          <span className="tl-year">{item.year}</span>
        </div>
        <p className="tl-event">{item.event}</p>
      </div>
      <div className="tl-dot">
        <div className="tl-dot-inner" />
      </div>
    </div>
  );
}

function TeamCard({ member, index, gradient }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`team-card ${inView ? 'in-view' : ''}`}
      style={{ transitionDelay: `${index * 90}ms` }}
    >
      <div className="team-card-top">
        <div className="team-avatar" style={{ background: gradient }}>
          {member.name.charAt(0)}
        </div>
        <div className="team-avatar-glow" style={{ background: gradient }} />
      </div>
      <h3 className="team-name">{member.name}</h3>
      <p className="team-title">{member.title}</p>
      <p className="team-bio">{member.bio}</p>
      <div className="team-card-footer">
        <a href="#linkedin" className="team-social" aria-label="LinkedIn">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
            <rect x="2" y="9" width="4" height="12"/>
            <circle cx="4" cy="4" r="2"/>
          </svg>
        </a>
        <a href="#twitter" className="team-social" aria-label="Twitter">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        </a>
      </div>
    </div>
  );
}

function ExpertiseCard({ expertise, index }) {
  const [ref, inView] = useInView();
  const colors = ['#2f80ed', '#00d4ff', '#10b981', '#7c3aed'];
  return (
    <div
      ref={ref}
      className={`team-card expertise-card ${inView ? 'in-view' : ''}`}
      style={{ transitionDelay: `${index * 90}ms` }}
    >
      <div className="team-card-top">
        <div className="team-avatar expertise-avatar" style={{ background: colors[index] }}>
          <span className="expertise-icon" style={{ color: 'white' }}>
            {expertiseIcons[index]}
          </span>
        </div>
        <div className="team-avatar-glow" style={{ background: colors[index] }} />
      </div>
      <h3 className="team-name">{expertise.title}</h3>
      <p className="team-bio">{expertise.description}</p>
    </div>
  );
}

function StatCard({ stat, index }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`stat-card ${inView ? 'in-view' : ''}`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="stat-number">
        <Counter to={stat.to} suffix={stat.suffix} />
      </div>
      <p className="stat-label">{stat.label}</p>
      <div className="stat-bar" />
    </div>
  );
}

function WhyCard({ card, icon, index }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`why-card ${inView ? 'in-view' : ''}`}
      style={{ transitionDelay: `${index * 90}ms` }}
    >
      <div className="why-icon">{icon}</div>
      <h3 className="why-title">{card.title}</h3>
      <p className="why-desc">{card.desc}</p>
      <div className="why-corner" aria-hidden />
    </div>
  );
}

export default About;